import React, { forwardRef, useState, type ForwardedRef } from 'react';
import AddIcon from '@atlaskit/icon/glyph/add';
import type { KeyedIssueType } from '@atlassian/jira-common-rest/src/internal/simplified/1.0/project/id/settings/issuetype/types';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { useIntl } from '@atlassian/jira-intl';
import { useAddIssueTypeContext } from '@atlassian/jira-project-settings-apps-add-issue-type';
import { useIssueTypesNavigationItems } from '@atlassian/jira-spa-apps-project-settings-issue-type/src/controllers/issue-types-navigation-items';
import { ADD_ISSUE_TYPE_OPTION } from '../../common/constants';
import type { ExtraOption } from '../../common/types';
import type { AddIssueTypeModalProps } from './add-issue-type-modal';
import { AsyncAddIssueModal } from './add-issue-type-modal/async';
import messages from './messages';
import TypeSelect, { type SelectRef, type Props } from './type-select';

type TypeSelectWithAddIssueTypeModalProps = Props & AddIssueTypeModalProps;

const TypeSelectWithAddIssueTypeModalInner = forwardRef(
	(
		{
			onNewIssueTypeCreated,
			cardTypes,
			extraOptions,
			onSelectCardType,
			...typeSelectProps
		}: TypeSelectWithAddIssueTypeModalProps,
		ref: ForwardedRef<SelectRef>,
	) => {
		const { formatMessage } = useIntl();
		const [_, { openAddIssueTypeModal }] = useAddIssueTypeContext();
		const [{ isAddIssueTypeDisabled }] = useIssueTypesNavigationItems();
		const [newTypeId, setNewTypeId] = useState<number | null>(null);
		const addIssueTypeOption: ExtraOption = {
			name: 'createIssueType',
			optionType: 'text',
			label: formatMessage(messages.addIssueType),
			value: ADD_ISSUE_TYPE_OPTION,
			onItemSelect: openAddIssueTypeModal,
			icon: <AddIcon size="small" label={formatMessage(messages.addIssueType)} />,
		};

		const handleOnNewIssueTypeCreated = (issueType: KeyedIssueType) => {
			setNewTypeId(issueType.id);
			onNewIssueTypeCreated?.(issueType);
		};

		const handleOnNewIssueTypeCreatedComplete = () => {
			if (newTypeId) {
				onSelectCardType(newTypeId);
				setNewTypeId(null);
			}
		};
		return (
			<>
				<AsyncAddIssueModal
					onNewIssueTypeCreated={handleOnNewIssueTypeCreated}
					onNewIssueTypeCreatedComplete={handleOnNewIssueTypeCreatedComplete}
				/>
				<TypeSelect
					{...typeSelectProps}
					cardTypes={cardTypes}
					extraOptions={
						isAddIssueTypeDisabled ? extraOptions : [addIssueTypeOption, ...(extraOptions ?? [])]
					}
					onSelectCardType={onSelectCardType}
					ref={ref}
				/>
			</>
		);
	},
);

export const TypeSelectWithAddIssueTypeModal = forwardRef(
	(props: TypeSelectWithAddIssueTypeModalProps, ref: ForwardedRef<SelectRef>) => (
		<JSErrorBoundary
			id="issueTypeCreatableTypeSelectComponent"
			packageName="jiraConfigInContextIssueTypes"
			teamName="jlove-makkuro"
			fallback={() => <TypeSelect {...props} ref={ref} />}
		>
			<TypeSelectWithAddIssueTypeModalInner {...props} ref={ref} />
		</JSErrorBoundary>
	),
);
